import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Facebook } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/data/site";

const Footer = () => {
  return (
    <footer className="bg-espresso text-ivory border-t-4 border-laurel" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img
              src="/logo-360.webp"
              srcSet="/logo-360.webp 360w, /logo-720.webp 720w"
              sizes="180px"
              width="1218"
              height="1129"
              alt="Savannah's House Residential Care Home — Est. Tyler, Texas"
              loading="lazy"
              decoding="async"
              className="h-44 lg:h-52 w-auto"
            />
            <p className="mt-4 text-ivory/70 text-sm leading-relaxed italic font-serif text-base">
              “{BUSINESS.tagline}”
            </p>
            <p className="mt-4 text-ivory/60 text-sm leading-relaxed">{BUSINESS.statement}</p>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              aria-label="Follow Savannah's House on Facebook"
              className="mt-5 inline-flex items-center gap-2.5 text-sm font-semibold text-ivory/80 hover:text-gold transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-ivory/10 hover:bg-gold hover:text-charcoal flex items-center justify-center transition-colors">
                <Facebook size={17} aria-hidden="true" />
              </span>
              Follow us on Facebook
            </a>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Visit & Contact</h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin size={16} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                <span>
                  {BUSINESS.city}
                  <br />
                  <span className="text-ivory/60">Serving Tyler & East Texas</span>
                </span>
              </li>
              <li>
                <a href={BUSINESS.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-2.5 hover:text-gold transition-colors">
                  <Phone size={16} className="shrink-0 text-gold" aria-hidden="true" />
                  {BUSINESS.phone}
                </a>
              </li>
              <li>
                <a href={BUSINESS.emailHref} data-testid="footer-email-link" className="flex items-center gap-2.5 hover:text-gold transition-colors text-xs break-all">
                  <Mail size={16} className="shrink-0 text-gold" aria-hidden="true" />
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-ivory/70">
                <Clock size={16} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                {BUSINESS.hours}
              </li>
            </ul>
          </div>

          <nav aria-label="Footer navigation">
            <h2 className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Explore</h2>
            <ul className="mt-4 grid grid-cols-1 gap-2 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-ivory/80 hover:text-gold transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Take the Next Step</h2>
            <p className="mt-4 text-sm text-ivory/70 leading-relaxed">
              Questions about availability, rooms, or veteran housing? We’re here to help.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <Link
                to="/contact?intent=tour"
                data-testid="footer-schedule-tour-button"
                className="bg-gold text-charcoal text-center text-sm font-bold px-5 py-3 rounded-full hover:bg-ivory transition-colors"
              >
                Schedule a Tour
              </Link>
              <Link
                to="/contact?intent=availability"
                data-testid="footer-check-availability-button"
                className="border border-ivory/40 text-center text-sm font-semibold px-5 py-3 rounded-full hover:border-gold hover:text-gold transition-colors"
              >
                Check Availability
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-ivory/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-ivory/60 text-center sm:text-left">{BUSINESS.copyright}</p>
          <nav aria-label="Legal" className="flex items-center gap-5 text-xs">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-ivory/70 hover:text-gold transition-colors">
              Privacy Policy
            </Link>
            <Link to="/accessibility" data-testid="footer-accessibility-link" className="text-ivory/70 hover:text-gold transition-colors">
              Accessibility
            </Link>
            <Link to="/contact" data-testid="footer-contact-link" className="text-ivory/70 hover:text-gold transition-colors">
              Contact
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
